import * as cmsApi from 'common/cms';
import type { DocTypeMap, HardwareProductDoc, PageDocTypes, SoftwareProductDoc } from 'modules/cms/types';
import { useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

type FullDocTypeMap = Omit<DocTypeMap, 'contentPage' | 'indexPage' | 'sectionPage'> & {
  multipage: PageDocTypes;
};
type LoadDocumentType = keyof FullDocTypeMap;
type DocTypeDictionary = {
  [key in keyof FullDocTypeMap]?: FullDocTypeMap[key][];
};
type DocTypeDictionaryMap = {
  /** Allow "undefined" as key in map to make it easier to work with */
  [key in LoadDocumentType]?: ReadonlyMap<string | number | undefined, FullDocTypeMap[key]>;
};

/**
 * Store loaded documents in sessionStorage to avoid reloads
 * Solved via proxy-object that reflects getters and persists on setters
 */
const _cacheKey = 'MinSide:cms-cache';
const _cacheObject = JSON.parse(sessionStorage.getItem(_cacheKey) || '{}') as DocTypeDictionary;
const loadedDocumentsMap = new Proxy(_cacheObject, {
  get: (target, prop: LoadDocumentType, receiver) => Reflect.get(target, prop, receiver),
  set: (target, prop: LoadDocumentType, value) => {
    target[prop] = value;
    // Stringify outside UI thread or in microtask?
    sessionStorage.setItem(_cacheKey, JSON.stringify(target));
    return true;
  },
});

/**
 * Pass a list of document types you need loaded from Sanity.
 * The hooks caches the loaded documents and only fetches uncached documents.
 * Updates redux store on success.
 */
export const useSanityDocuments = <T extends LoadDocumentType[]>(documentTypes: T) => {
  type ResType = Pick<Required<DocTypeDictionary>, T[number]>;

  return useSWRImmutable<ResType>(['useSanityDocuments', ...documentTypes], async () => {
    // Get documents from cache that have already been loaded
    // Add type on non loaded documents to list
    const needsLoading: LoadDocumentType[] = [];
    const cachedResponse = documentTypes.reduce((acc, type: T[number]) => {
      if (loadedDocumentsMap[type]) {
        acc[type] = loadedDocumentsMap[type];
      } else {
        needsLoading.push(type);
      }
      return acc;
    }, {} as DocTypeDictionary);
    // Fetch non loaded documents
    const loadedData = await Promise.all(
      needsLoading.map((type) => documentLoaderMap[type]().then((data) => [type, data] as const)),
    );
    // Displatch actions and cache data
    loadedData.forEach(([type, data]) => {
      // @ts-expect-error: we know these are the correct types
      loadedDocumentsMap[type] = cachedResponse[type] = data;
    });

    return cachedResponse as ResType;
  });
};

/**
 * Like `useSanityDocuments`, but returns a lookup object with Maps for the loaded document types based on their `_id` (and `commercialProductId` if available).
 * @example
 * // lookup via docId
 * useSanityDocumentsMap(['softwareProduct']).softwareProduct.get('doc-id')
 * // lookup via commercialProductId
 * useSanityDocumentsMap(['softwareProduct']).softwareProduct.get(234)
 */
export const useSanityDocumentsMap = <T extends LoadDocumentType[]>(documentTypes: T) => {
  type ResType = Pick<Required<DocTypeDictionaryMap>, T[number]>;
  const { data: docsRecord, isLoading } = useSanityDocuments(documentTypes);

  // biome-ignore lint/correctness/useExhaustiveDependencies: docType array is already dependency to useSanityDocuments
  const data = useMemo(() => {
    if (!docsRecord) return undefined;
    // create the result object
    const lookup = documentTypes.reduce((acc, type: T[number]) => {
      const list = docsRecord[type];
      // @ts-expect-error: treat as writable Map here
      acc[type] = new Map(list.map((doc) => [doc._id, doc]));
      // also map by commercialProductId if available in doc type
      if (list[0] && 'commercialProductId' in list[0]) {
        (list as Array<SoftwareProductDoc | HardwareProductDoc>).forEach((doc) => {
          // @ts-expect-error: treat at writable Map here
          acc[type].set(doc.commercialProductId, doc);
        });
      }
      return acc;
    }, {} as DocTypeDictionaryMap);
    return lookup as ResType;
  }, [docsRecord]);
  return { data, isLoading };
};

/**
 * @example
 * // Get a specific document by type and field value
 * const doc = useSpecificSanityDocument('softwareProduct', 'commercialProductId', 234);
 */
export const useSpecificSanityDocument = <T extends LoadDocumentType, K extends keyof FullDocTypeMap[T]>(
  documentType: T,
  field: K,
  value: FullDocTypeMap[T][K] | undefined,
) => {
  const docs = useSanityDocuments<T[]>([documentType]).data?.[documentType] as undefined | FullDocTypeMap[T][];
  if (value == null) return;
  return docs?.find((d) => d[field] === value);
};

const documentLoaderMap = {
  softwareProduct: cmsApi.getSoftwareProducts,
  hardwareProduct: cmsApi.getHardwareProducts,
  multipage: cmsApi.getPages,
  mypageCampaign: cmsApi.getCampaignPages,
  channel: cmsApi.getDocumentsByType.bind(null, 'channel'),
  product: cmsApi.getDocumentsByType.bind(null, 'product'),
  mypageProductBundle: cmsApi.getProductBundles,
  regrade: cmsApi.getRegradeProcedures,
  mypageBundleConfig: cmsApi.getBundleConfigs,
} satisfies Record<LoadDocumentType, () => Promise<any>>;
